<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { INITIAL_TEAMS, INITIAL_PLAYERS, INITIAL_EVENTS } from '@/services/mockData';
import { useToastNotification } from '@/composables/useToastNotification';
import AppBadge from '@/components/ui/AppBadge.vue';
import { NCard, NButton, NTag } from 'naive-ui';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle2,
  XCircle,
  Shirt,
  Flame,
  Award,
  Bell,
  Target,
  ChevronRight
} from 'lucide-vue-next';

const { t } = useI18n();
const { activeTenant } = useTenant();
const { notifySuccess, notifyInfo } = useToastNotification();

// Player profile context
const myPlayer = computed(() => INITIAL_PLAYERS[0]); // Cole Vance #24
const myTeam = computed(() => INITIAL_TEAMS[0]);

// RSVP status for upcoming event
const nextEvent = computed(() => INITIAL_EVENTS[0]);
const myRsvp = ref<'ATTENDING' | 'DECLINED' | 'PENDING'>('ATTENDING');

const handleRsvp = (status: 'ATTENDING' | 'DECLINED') => {
  myRsvp.value = status;
  if (status === 'ATTENDING') {
    notifySuccess(t('playerDashboard.rsvpSuccess'), t('playerDashboard.rsvpSuccessMsg'));
  } else {
    notifyInfo(t('playerDashboard.rsvpDeclined'), t('playerDashboard.rsvpDeclinedMsg'));
  }
};
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <!-- Player Hero Banner -->
    <div class="p-6 rounded-3xl bg-gradient-to-r from-sky-950 via-slate-900 to-indigo-950 border border-sky-500/20 shadow-2xl relative overflow-hidden">
      <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div class="flex items-center gap-4">
          <div class="w-16 h-16 rounded-2xl bg-gradient-to-tr from-sky-500 to-indigo-600 flex items-center justify-center text-white font-extrabold text-2xl shadow-lg shadow-sky-500/25">
            #{{ myPlayer.jerseyNumber }}
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h1 class="text-2xl font-extrabold text-white tracking-tight">
                {{ t('playerDashboard.welcome', { name: myPlayer.firstName }) }}
              </h1>
              <AppBadge variant="position">{{ myPlayer.position }} / {{ myPlayer.secondaryPosition }}</AppBadge>
            </div>
            <p class="text-xs text-slate-300 mt-1">
              {{ myTeam.name }} &bull; Bats: <strong class="text-white">{{ myPlayer.bats }}</strong> &bull; Throws: <strong class="text-white">{{ myPlayer.throws }}</strong>
            </p>
          </div>
        </div>

        <!-- Quick Player Stat Pills -->
        <div class="flex items-center gap-2">
          <div class="px-3.5 py-2 rounded-xl bg-slate-900/80 border border-slate-800 text-center">
            <span class="text-[10px] uppercase font-bold text-slate-400 block">{{ t('playerDashboard.battingAvg') }}</span>
            <span class="text-sm font-mono font-extrabold text-emerald-400">.348</span>
          </div>
          <div class="px-3.5 py-2 rounded-xl bg-slate-900/80 border border-slate-800 text-center">
            <span class="text-[10px] uppercase font-bold text-slate-400 block">{{ t('playerDashboard.exitVelo') }}</span>
            <span class="text-sm font-mono font-extrabold text-sky-400">92.4 MPH</span>
          </div>
          <div class="px-3.5 py-2 rounded-xl bg-slate-900/80 border border-slate-800 text-center">
            <span class="text-[10px] uppercase font-bold text-slate-400 block">{{ t('playerDashboard.ops') }}</span>
            <span class="text-sm font-mono font-extrabold text-amber-400">1.020</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Player Grid: Next Event & Action Center -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <!-- Next Event Card with Instant RSVP (7 cols) -->
      <div class="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-5">
        <div class="flex items-center justify-between pb-3 border-b border-slate-800">
          <div class="flex items-center gap-2">
            <Calendar class="w-4 h-4 text-sky-400" />
            <h2 class="text-sm font-bold uppercase tracking-wider text-slate-100">{{ t('playerDashboard.nextGame') }}</h2>
          </div>
          <AppBadge variant="accent">{{ t('playerDashboard.firstPitch') }}</AppBadge>
        </div>

        <div>
          <h3 class="text-lg font-extrabold text-slate-100">{{ nextEvent.title }}</h3>
          <p class="text-xs text-slate-400 mt-0.5">VS: <strong class="text-sky-300">{{ nextEvent.opponent }}</strong> ({{ nextEvent.homeAway }})</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div class="p-3 bg-slate-950 rounded-xl border border-slate-800/80 flex items-center gap-2.5">
            <Clock class="w-4 h-4 text-sky-400 shrink-0" />
            <div>
              <span class="text-[10px] text-slate-500 uppercase block font-semibold">{{ t('playerDashboard.reportTime') }}</span>
              <span class="text-xs font-bold text-slate-200">{{ t('playerDashboard.reportTimeVal') }}</span>
            </div>
          </div>
          <div class="p-3 bg-slate-950 rounded-xl border border-slate-800/80 flex items-center gap-2.5">
            <Shirt class="w-4 h-4 text-amber-400 shrink-0" />
            <div>
              <span class="text-[10px] text-slate-500 uppercase block font-semibold">{{ t('playerDashboard.uniform') }}</span>
              <span class="text-xs font-bold text-slate-200">{{ nextEvent.uniformColor || 'Navy & Gold' }}</span>
            </div>
          </div>
        </div>

        <div class="p-3 bg-slate-950 rounded-xl border border-slate-800/80 flex items-start gap-2.5">
          <MapPin class="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <span class="text-[10px] text-slate-500 uppercase block font-semibold">{{ t('playerDashboard.location') }}</span>
            <span class="text-xs text-slate-300 font-medium">{{ nextEvent.location }}</span>
          </div>
        </div>

        <!-- Player RSVP Action -->
        <div class="pt-2 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <span class="text-xs text-slate-400">{{ t('playerDashboard.attendance') }}</span>
            <span :class="['ml-2 text-xs font-bold font-mono', myRsvp === 'ATTENDING' ? 'text-emerald-400' : 'text-rose-400']">
              {{ myRsvp === 'ATTENDING' ? t('playerDashboard.confirmedIn') : t('playerDashboard.declinedOut') }}
            </span>
          </div>

          <div class="flex items-center gap-2">
            <button
              type="button"
              @click="handleRsvp('ATTENDING')"
              :class="[
                'px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-sm',
                myRsvp === 'ATTENDING'
                  ? 'bg-emerald-500 text-slate-950 font-extrabold ring-2 ring-emerald-400/30'
                  : 'bg-slate-950 text-slate-300 border border-slate-800 hover:bg-slate-800'
              ]"
            >
              <CheckCircle2 class="w-4 h-4" />
              <span>{{ t('playerDashboard.imIn') }}</span>
            </button>
            <button
              type="button"
              @click="handleRsvp('DECLINED')"
              :class="[
                'px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5',
                myRsvp === 'DECLINED'
                  ? 'bg-rose-500 text-white font-extrabold ring-2 ring-rose-400/30'
                  : 'bg-slate-950 text-slate-400 border border-slate-800 hover:bg-slate-800'
              ]"
            >
              <XCircle class="w-4 h-4" />
              <span>{{ t('playerDashboard.cannotAttend') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Coach Notes & Lineup Slot (5 cols) -->
      <div class="lg:col-span-5 space-y-4">
        <!-- Starting Lineup Status -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-3">
          <div class="flex items-center justify-between">
            <h3 class="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Target class="w-4 h-4 text-sky-400" />
              {{ t('playerDashboard.lineupAssignment') }}
            </h3>
            <NTag type="success" size="small" :bordered="false" round>{{ t('playerDashboard.startingLineup') }}</NTag>
          </div>

          <div class="p-3.5 bg-slate-950 rounded-2xl border border-slate-800 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <span class="w-8 h-8 rounded-xl bg-sky-500/20 text-sky-400 font-mono font-extrabold text-sm flex items-center justify-center">
                #2
              </span>
              <div>
                <p class="text-xs font-bold text-slate-200">{{ t('playerDashboard.batterHole') }}</p>
                <p class="text-[11px] text-slate-400">{{ t('playerDashboard.posShortstop') }}</p>
              </div>
            </div>
            <router-link to="/dashboard/lineup" class="text-xs text-sky-400 hover:text-sky-300 font-semibold flex items-center gap-0.5">
              {{ t('playerDashboard.card') }} <ChevronRight class="w-3.5 h-3.5" />
            </router-link>
          </div>
        </div>

        <!-- Coach Holloway's Bulletin -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-3">
          <div class="flex items-center gap-2">
            <Bell class="w-4 h-4 text-amber-400" />
            <h3 class="text-xs font-bold text-slate-200 uppercase tracking-wider">{{ t('playerDashboard.coachBulletin') }}</h3>
          </div>
          <div class="p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800 text-xs text-slate-300 space-y-2">
            <p class="italic text-slate-200">
              {{ t('playerDashboard.coachQuote') }}
            </p>
            <p class="text-[10px] text-slate-500 font-semibold">{{ t('playerDashboard.coachAuthor') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>